import { useRef, useState } from 'react';

// utils
import { type CollectionChoiceInfo, VEDBackendApi } from '@virginexperiencedays/backend';

const api = new VEDBackendApi({
  apiKey: process.env.NEXT_PUBLIC_BACKEND_API_KEY_FOR_COLLECTIONS,
  baseUrl: process.env.NEXT_PUBLIC_BACKEND_API_BASE_URL,
});

export const useFetchCollectionChoiceInfo = () => {
  const [isLoadingCollectionChoiceInfo, setIsLoadingCollectionChoiceInfo] = useState(true);
  const [collectionChoiceInfo, setCollectionChoiceInfo] = useState<CollectionChoiceInfo | null>(
    null
  );
  const activeSlugRef = useRef<string>('');

  const clearCollectionChoiceInfo = () => {
    setCollectionChoiceInfo(null);
  };

  const triggerFetchCollectionChoiceInfo = async (slug: string) => {
    if (!slug.length) {
      clearCollectionChoiceInfo();
      activeSlugRef.current = '';
      return;
    }

    setIsLoadingCollectionChoiceInfo(true);
    clearCollectionChoiceInfo(); // clear old data before loading new data
    activeSlugRef.current = slug;

    const result = await fetchCollectionChoiceInfo(slug, api);
    // assign the result only if the data is up to selected slug, otherwise discard stale data.
    if (result?.id && result?.slug === activeSlugRef.current) {
      setCollectionChoiceInfo(result);
    }
    setIsLoadingCollectionChoiceInfo(false);
  };

  return {
    collectionChoiceInfo,
    triggerFetchCollectionChoiceInfo,
    isLoadingCollectionChoiceInfo,
    setIsLoadingCollectionChoiceInfo,
    clearCollectionChoiceInfo,
  };
};

/**
 * Fetches CollectionChoiceInfo from the new Product API by product slug.
 *
 * @param slug - The product `slug`.
 * @param apiInstance - Optional instance of the `VEDBackendApi`.
 * @returns A promise that resolves to `CollectionChoiceInfo` object.
 */
export async function fetchCollectionChoiceInfo(
  slug: string,
  apiInstance: VEDBackendApi
): Promise<CollectionChoiceInfo> {
  try {
    return await apiInstance.bff.collectionChoiceInfo.get(slug);
  } catch (error) {
    console.warn(
      `Error fetching collection choice info for ${slug}`,
      error instanceof Error ? error.message : error
    );

    return null;
  }
}
